import React from 'react'

// material ui
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import InputAdornment from '@mui/material/InputAdornment'
import Alert from '@mui/material/Alert'

// shared components
import ModalWrapper from 'components/shared/ModalWrapper'
import FormButton from 'components/atoms/FormButton'

const FinancialAidApplicationModal = props => {
  const {
    sessions,
    form,
    inputErrors,
    mutationLoading,
    onChangeFormField,
    onClickSubmit
  } = props

  const modalId = 'FinancialAidApplication'

  // Calculate previous tax year
  const previousTaxYear = new Date().getFullYear() - 1

  // Filter to only sessions with financial aid open (date is today or in the past)
  const openSessions = React.useMemo(() => {
    if (!sessions || sessions.length === 0) return []

    const now = new Date()
    return sessions.filter(s => new Date(s.financialAidOpenAt) <= now)
  }, [sessions])

  // Get deduplicated locations from all open sessions that have financial aid tiers
  // Deduplicate by name since the same location may appear in multiple sessions with different IDs
  const locations = React.useMemo(() => {
    if (openSessions.length === 0) return []

    const locationMap = new Map()
    openSessions.forEach(session => {
      session?.locations?.forEach(loc => {
        if (loc.financialAidTier && !locationMap.has(loc.name)) {
          locationMap.set(loc.name, loc)
        }
      })
    })

    return Array.from(locationMap.values()).sort((a, b) =>
      `${a.name} ${a.city} ${a.stateName}`.localeCompare(`${b.name} ${b.city} ${b.stateName}`)
    )
  }, [openSessions])

  // Check if required fields are filled
  const isFormValid =
    form.locationId && form.familySize && form.adjustedGrossIncome

  return (
    <ModalWrapper
      modalId={modalId}
      title='Financial Aid Application'
      closeButton
    >
      <Box component='form'>
        <Typography variant='body1' mb={3}>
          Please provide the following information from your {previousTaxYear}{' '}
          tax return:
        </Typography>

        <TextField
          fullWidth
          select
          label='Camp Location'
          value={form.locationId || ''}
          onChange={onChangeFormField('locationId')}
          error={!!inputErrors.locationId}
          helperText={inputErrors.locationId}
          margin='normal'
          required
        >
          <MenuItem value=''>
            <em>Select a location</em>
          </MenuItem>
          {locations.map(location => (
            <MenuItem key={location.id} value={location.id}>
              {location.name} - {location.city}, {location.stateName}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          select
          label={`Number of Individuals Claimed on ${previousTaxYear} Taxes`}
          value={form.familySize || ''}
          onChange={onChangeFormField('familySize')}
          error={!!inputErrors.familySize}
          helperText={
            inputErrors.familySize || 'Family size must be 2, 3, 4, or 5+'
          }
          margin='normal'
          required
        >
          <MenuItem value=''>
            <em>Select family size</em>
          </MenuItem>
          <MenuItem value={2}>2</MenuItem>
          <MenuItem value={3}>3</MenuItem>
          <MenuItem value={4}>4</MenuItem>
          <MenuItem value={5}>5+</MenuItem>
        </TextField>

        <TextField
          fullWidth
          type='number'
          label={`Adjusted Gross Income (AGI) from ${previousTaxYear} Taxes`}
          value={form.adjustedGrossIncome || ''}
          onChange={onChangeFormField('adjustedGrossIncome')}
          error={!!inputErrors.adjustedGrossIncome}
          helperText={inputErrors.adjustedGrossIncome}
          margin='normal'
          required
          InputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>
          }}
        />

        <TextField
          fullWidth
          multiline
          rows={4}
          label='Special Circumstances (if AGI exceeds income limits)'
          value={form.specialCircumstances || ''}
          onChange={onChangeFormField('specialCircumstances')}
          error={!!inputErrors.specialCircumstances}
          helperText={
            inputErrors.specialCircumstances ||
            'Optional: Explain any special circumstances if your income exceeds the published limits'
          }
          margin='normal'
        />

        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          mt={1}
          mb={2}
          fontStyle='italic'
        >
          I attest that all the information I have provided in this form is true
          to the best of my knowledge. I agree that I will, if asked, supply
          documentation of my financial circumstances to support my financial
          aid application (such as tax forms, letters of change in employment,
          etc.) and that failure to supply such documentation upon request may
          result in my financial aid application being denied. I also agree
          that, if any information I supply is found to be falsified, it could
          result in my camper not being allowed to continue at Camp this season
          and/or in the future.
        </Typography>

        {inputErrors.base && inputErrors.base.length > 0 && (
          <Box mt={2}>
            {inputErrors.base.map((error, index) => (
              <Alert key={index} severity='error' sx={{ mb: 1 }}>
                {error}
              </Alert>
            ))}
          </Box>
        )}

        <Box mt={3} textAlign='center'>
          <FormButton
            variant='contained'
            color='secondary'
            onClick={onClickSubmit}
            disabled={mutationLoading || !isFormValid}
          >
            {mutationLoading ? 'Submitting...' : 'Submit Application'}
          </FormButton>
        </Box>
      </Box>
    </ModalWrapper>
  )
}

export default FinancialAidApplicationModal
